<template>
  <nav
    class="pagination"
    aria-label="Pagination"
  >
    <ul>
      <!-- Previous Page Link -->
      <li
        v-if="currentPage > 1"
        data-testid="prev-button"
      >
        <button
          aria-label="Previous"
          @click="selectPage(currentPage - 1)"
        >
          <Icon
            name="angle-left"
            title="Next"
            class="pull-left"
          />
          <span
            v-translate
            aria-hidden="true"
          >
            Previous
          </span>
        </button>
      </li>

      <!-- Page Numbers -->
      <li
        v-for="pageNumber in paginationRange"
        :key="pageNumber"
        :class="{ active: pageNumber === currentPage, 'ellipsis': !pageNumber }"
      >
        <button
          v-if="pageNumber && pageNumber !== currentPage"
          class="button-round"
          @click="selectPage(pageNumber)"
        >
          {{ pageNumber }}
        </button>
        <button
          v-else-if="pageNumber"
          class="button-round"
        >
          {{ pageNumber }}
        </button>
        <span
          v-else
          class="ellipsis"
        >
          …
        </span>
      </li>

      <!-- Next Page Link -->
      <li
        v-if="currentPage < pagesCount"
        data-testid="next-button"
      >
        <button
          aria-label="Next"
          @click="selectPage(currentPage + 1)"
        >
          <span
            v-translate
            aria-hidden="true"
          >
            Next
          </span>
          <Icon
            name="angle-right"
            title="Next"
            class="pull-right"
          />
        </button>
      </li>
    </ul>
  </nav>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import Icon from './Icon.vue'

@Options({
  props: {
    currentPage: { type: Number, required: true },
    pagesCount: { type: Number, required: true },
  },
  components: {
    Icon,
  },
  computed: {
    paginationRange() {
      const range = [];

      let rangeStart = this.currentPage - 2;
      let rangeEnd = this.currentPage + 2;

      // Adjust the range when the current page is near the start
      if (this.currentPage <= 4 && this.pagesCount >= 5) {
        rangeEnd = 5;
      }

      if (this.currentPage >= this.pagesCount - 3 && this.pagesCount >= 5) {
        const offset = this.pagesCount - this.currentPage;
        rangeStart = this.currentPage - (4 - offset);
        const isLastOrSecondLastPage = this.currentPage === this.pagesCount || this.currentPage === this.pagesCount - 1;
        rangeEnd = isLastOrSecondLastPage ? this.currentPage : this.pagesCount - 1;
      }

      // Ensure the range does not go out of bounds
      rangeStart = Math.max(rangeStart, 2);
      rangeEnd = Math.min(rangeEnd, this.pagesCount - 1);

      // Always include the first page
      range.push(1);

      // Include ellipsis if there's a gap between the first page and the range start
      if (rangeStart > 2) {
        range.push(null);
      }

      // Add the calculated range of page numbers
      for (let i = rangeStart; i <= rangeEnd; i++) {
        range.push(i);
      }

      // Include ellipsis if there's a gap between the range end and the last page
      if (rangeEnd < this.pagesCount - 1) {
        range.push(null);
      }

      // Always include the last page
      range.push(this.pagesCount);

      return range;
    },

  },
})
export default class PaginationDots extends Vue {
  currentPage!: number;
  pagesCount!: number;
  paginationRange!: [];

  selectPage(pageNumber: number) {
    this.$emit('selectPage', pageNumber);
  }
}
</script>
